import { rgba } from 'polished';
import styled from 'styled-components';
import { getPublicPath } from 'utils/paths';
import {
  color,
  fontFamily,
  fontSize,
  fontWeight,
  screen,
  spacing,
  container,
} from '../../../theme';

export const StyledHeroInfo = styled.section`
  grid-area: info;
  display: flex;
  flex-direction: column;
  text-align: center;

  @media (min-width: ${screen('md')}) and (max-width: ${screen('lgMax')}) {
    max-width: ${container('md')};
  }

  @media (min-width: ${screen('lg')}) {
    text-align: left;
  }
`;

export const StyledHeroInfoDescription = styled.div`
  font-family: ${fontFamily('serif')};
  font-size: ${fontSize('xl')};
  font-weight: ${fontWeight('light')};
  line-height: 1.2;
  margin: 0;
  @media (max-width: ${screen('mdMax')}) {
    font-size: ${fontSize('lg')};
    font-weight: ${fontWeight('regular')};
  }

  a {
    color: ${color('link_inverse')};
    &:hover {
      color: ${color('link_inverse_hover')};
    }
    &:active {
      color: ${color('link_inverse_active')};
    }
  }

  ul,
  ol {
    padding-left: 1em;
    text-align: left;
  }

  li {
    list-style: inherit;
    &:not(:last-child) {
      margin-bottom: ${spacing('sm')};
    }
  }

  ol li::marker {
    color: ${color('text_subtle_inverse')};
    font-weight: ${fontWeight('black')};
  }
`;

export const StyledHeroInfoContainer = styled.div`
  margin-top: -${spacing('lg')};
  margin-bottom: ${spacing('lg')};
  margin-left: -${spacing('md')};
  margin-right: -${spacing('md')};
  padding-top: ${spacing('lg')};
  padding-left: ${spacing('md')};
  padding-right: ${spacing('md')};
  background-color: ${color('midnight_900')};
  position: relative;

  > * {
    z-index: 1;
    position: relative;
  }

  @media (min-width: ${screen('md')}) {
    background-color: transparent;
    margin: 0;
    padding: 0;
    z-index: 0;

    > * {
      position: static;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: linear-gradient(
      147deg,
      ${color('purple_900')} 0%,
      ${rgba('#1e076e', 0.6)} 70%,
      transparent 100%
    );
    mix-blend-mode: hard-light;
    z-index: 1;
    @media (min-width: ${screen('md')}) {
      display: none;
    }
  }
`;

export const StyledHeroInfoBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.2;
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${color('emerald_500')};
    mix-blend-mode: darken;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: black;
    mix-blend-mode: lighten;
  }

  div {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${() =>
      `url('${getPublicPath('/images/jobOffer/heroBg.jpg')}')`};
    background-size: cover;
    background-position: center center;
    filter: grayscale(100%);
    z-index: -1;
  }
  @media (min-width: ${screen('md')}) {
    display: none;
  }
`;

export const StyledHeroInfoCompany = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: ${spacing('sm')};
  margin-bottom: ${spacing('md')};
  border-bottom: 1px solid ${color('midnight_200')};

  @media (min-width: ${screen('md')}) {
    display: none;
  }
`;

export const StyledHeroInfoCompanyLogo = styled.img`
  display: block;
  max-height: 3rem;
  object-fit: contain;

  @media (min-width: ${screen('md')}) {
    max-height: 4.5rem;
  }
`;

export const StyledHeroInfoCompanyArea = styled.div`
  font-size: ${fontSize('xs')};
  font-weight: ${fontWeight('black')};
  text-transform: uppercase;
  margin-left: ${spacing('md')};
  padding-left: ${spacing('md')};
  border-left: 1px solid ${rgba('white', 0.5)};
  display: flex;
  align-items: center;
`;

export const StyledHeroInfoFooter = styled.footer`
  display: flex;
  justify-content: space-between;
  padding-top: ${spacing('md')};
  margin-top: ${spacing('md')};
  border-top: 1px solid ${color('midnight_700')};

  @media (min-width: ${screen('md')}) {
    display: none;
  }
`;

export const StyledHeroInfoManagement = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  span {
    color: ${color('text_subtle')};
    font-size: ${fontSize('xs')};
    font-weight: ${fontWeight('medium')};
    line-height: 1.33;
  }
  img {
    display: block;
    max-height: 1.25rem;
  }
`;

export const StyledHeroInfoScout = styled.div`
  display: flex;
  align-items: center;

  .scout-avatar {
    margin-right: ${spacing('xs')};
  }
`;

export const StyledHeroInfoScoutInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  span {
    color: ${color('text_subtle')};
    font-size: ${fontSize('xs')};
    font-weight: ${fontWeight('bold')};
    line-height: 1.33;
    text-transform: uppercase;
  }
  a {
    font-size: ${fontSize('sm')};
    font-weight: ${fontWeight('bold')};
    color: ${color('link_inverse')};
  }
`;

export const StyledHeroClosedBadge = styled.div`
  margin-bottom: ${spacing('xs')};
`;


export const StyledHeroLanguageBadge = styled.div`
  margin-bottom: ${spacing('md')};
  
  span {
    display: inline-block;
    color: ${color('midnight_200')};
    background-color: ${color('midnight_900')};
    padding: ${spacing('xs')};
    font-size: ${fontSize('xs')};
    font-weight: ${fontWeight('extraBold')};
  }
`;